import { useState, type CSSProperties } from 'react';
import { getStrings, type Lang } from '../lib/i18n';
import { IPhoneFrame } from './IPhoneFrame';
import { localizedScreenshot } from '../lib/screenshots';

type Vars = CSSProperties & Record<`--${string}`, string | number>;

// Structural data (screenshots + numbers + accent); titles come from the locale strings.
const stepMeta = [
  { number: '01', screenshot: '/screenshots/home.png', c: 'var(--pop-5)' },
  { number: '02', screenshot: '/screenshots/analytics.png', c: 'var(--pop-2)' },
  { number: '03', screenshot: '/screenshots/muscles.png', c: 'var(--pop-3)' },
  { number: '04', screenshot: '/screenshots/exercises.png', c: 'var(--pop-1)' },
  { number: '05', screenshot: '/screenshots/calendar.png', c: 'var(--pop-4)' },
  { number: '06', screenshot: '/screenshots/nutrition.png', c: 'var(--pop-5)' },
  { number: '07', screenshot: '/screenshots/training-log.png', c: 'var(--pop-2)' },
  { number: '08', screenshot: '/screenshots/reaction.png', c: 'var(--pop-3)' },
  { number: '09', screenshot: '/screenshots/activity.png', c: 'var(--pop-1)' },
  { number: '10', screenshot: '/screenshots/rank.png', c: 'var(--pop-4)' },
];

export const Product = ({ lang = 'en' as Lang }: { lang?: Lang }) => {
  const t = getStrings(lang).product;
  const steps = stepMeta.map((m, i) => ({ ...m, screenshot: localizedScreenshot(m.screenshot, lang), title: t.steps[i] }));
  const [activeStep, setActiveStep] = useState(0);

  // Plain render function (not a nested component) so buttons never remount.
  const renderStep = (index: number) => {
    const step = steps[index];
    const active = activeStep === index;
    return (
      <button
        key={index}
        type="button"
        onClick={() => setActiveStep(index)}
        aria-pressed={active}
        className={`card-chunky is-hoverable w-full text-left px-4 py-3 md:py-3.5 ${active ? 'bg-[var(--brand-soft)]' : ''}`}
        style={{ '--edge': active ? 'var(--brand)' : 'var(--line)' } as Vars}
      >
        <span className="flex items-center gap-3.5">
          <span
            className="chip-icon flex-shrink-0 w-11 h-11 font-heading text-lg font-black"
            style={{ '--chip': active ? 'var(--brand)' : step.c } as Vars}
          >
            {step.number}
          </span>
          <span className={`font-heading text-base md:text-lg font-black leading-tight ${active ? 'text-[var(--brand-ink)]' : 'text-ink'}`}>
            {step.title}
          </span>
        </span>
      </button>
    );
  };

  return (
    <section className="relative overflow-x-clip py-20 md:py-28 bg-elevated/60">
      <div className="container mx-auto px-5 sm:px-6">
        <div className="text-center mb-12 md:mb-16" data-reveal>
          <h2 className="font-heading text-4xl md:text-5xl font-black text-ink tracking-tight">{t.heading}</h2>
        </div>

        <div className="max-w-6xl mx-auto grid gap-8 md:grid-cols-[1fr_auto_1fr] md:gap-10 items-center">
          <div className="space-y-3.5 order-2 md:order-1" data-reveal="left">
            {[0, 1, 2, 3, 4].map(renderStep)}
          </div>

          {/* One phone; every screenshot is mounted once and toggled by opacity
              (no remount / reload when switching steps). */}
          <div className="order-1 md:order-2 flex justify-center" data-reveal="pop">
            <div className="relative">
              <div className="blob -inset-16" aria-hidden="true" />
              <div className="phone-lift relative">
                <IPhoneFrame className="w-[210px] md:w-[240px]">
                  {steps.map((step, i) => (
                    <img
                      key={step.number}
                      src={step.screenshot}
                      alt={step.title}
                      loading={i === 0 ? 'eager' : 'lazy'}
                      aria-hidden={i !== activeStep}
                      className={`transition-opacity duration-300 ${i === activeStep ? 'opacity-100' : 'opacity-0'}`}
                    />
                  ))}
                </IPhoneFrame>
              </div>
            </div>
          </div>

          <div className="space-y-3.5 order-3" data-reveal="right">
            {[5, 6, 7, 8, 9].map(renderStep)}
          </div>
        </div>
      </div>
    </section>
  );
};
